import type React from "react";
import { useState, useMemo, useEffect } from "react";
import styles from "./ApplianceSelector.module.scss";
import Button from "../../../../../common/components/xepp/button/Button";
import CommonDialog from "../../../../../common/components/dialog/CommonDialog";
import { Appliance } from "../../../../../model/appliance";
import { PenIcon, SearchIcon } from "../../../../../assets/icons";
import { AssessmentApplianceSubmissionModel, SelectedAppliance } from "../../request.type";
import { ApplianceEditModal } from "./ApplianceEditModal";
import { useGetAppliancesQuery } from "../../../../../service/appliance-service";
import CommonLoadingSpinner from "../../../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import { useCarryOutPhysicalLoadAssessmentMutation, useSaveContinueAssessmentMutation } from "../../../../../service/leads-service";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../../utils/error-handler";
import { AssessmentAppliance } from "../../../../../model/leads/leads";

interface ApplianceSelectorProps {
	recommendationId: string;
	previouslySelected?: AssessmentAppliance[];
	onSubmit?: (data: AssessmentApplianceSubmissionModel) => void;
	onComplete: () => void;
	onClose: () => void;
}

const ApplianceSelector: React.FC<ApplianceSelectorProps> = ({ recommendationId, previouslySelected = [], onSubmit, onComplete, onClose }) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedAppliances, setSelectedAppliances] = useState<SelectedAppliance[]>([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingAppliance, setEditingAppliance] = useState<{
		appliance: Appliance;
		data: SelectedAppliance;
		isNew: boolean;
	} | null>(null);

	const { data = [], isLoading, isError } = useGetAppliancesQuery("");
	const [carryoutPhysicalLoadAssessment, { isLoading: processingAssessment }] = useCarryOutPhysicalLoadAssessmentMutation();
	const [saveAssessment, { isLoading: savingAssessment }] = useSaveContinueAssessmentMutation();

	const mappedAppliances = useMemo(() => {
		return previouslySelected.map(appliance => ({
			...appliance,
			id: appliance.applianceId || appliance.id,
		}));
	}, [previouslySelected]);

	useEffect(() => {
		if (mappedAppliances.length > 0) {
			setSelectedAppliances(mappedAppliances as unknown as SelectedAppliance[]);
		}
	}, [mappedAppliances]);

	// Check if appliance is already selected
	const isApplianceSelected = (applianceId: number) => {
		return selectedAppliances.some(selected => selected.id === applianceId);
	};

	const filteredAppliances = useMemo(() => {
		const filtered = data.filter(appliance => appliance.name.toLowerCase().includes(searchTerm.toLowerCase()));

		// Sort so selected appliances appear first
		return filtered.sort((a, b) => {
			const aSelected = isApplianceSelected(a.id);
			const bSelected = isApplianceSelected(b.id);

			// If one is selected and other isn't, prioritize selected
			if (aSelected && !bSelected) return -1;
			if (!aSelected && bSelected) return 1;

			return 0;
		});
	}, [searchTerm, selectedAppliances, data]);

	// Get selected appliance data
	const getSelectedApplianceData = (applianceId: number) => {
		return selectedAppliances.find(selected => selected.id === applianceId);
	};

	// Handle add appliance
	const handleAddAppliance = (appliance: Appliance) => {
		setEditingAppliance({
			appliance,
			data: { id: appliance.id, quantity: 1, usage: 1 },
			isNew: true,
		});
		setIsModalOpen(true);
	};

	// Handle edit appliance
	const handleEditAppliance = (appliance: Appliance) => {
		const existingData = getSelectedApplianceData(appliance.id);
		if (existingData) {
			setEditingAppliance({
				appliance,
				data: existingData,
				isNew: false,
			});
			setIsModalOpen(true);
		}
	};

	// Handle save appliance from modal
	const handleSaveAppliance = (data: SelectedAppliance) => {
		setSelectedAppliances(prev => {
			const existing = prev.find(item => item.id === data.id);
			if (existing) {
				return prev.map(item => (item.id === data.id ? data : item));
			} else {
				return [...prev, data];
			}
		});
		setIsModalOpen(false);
		setEditingAppliance(null);
	};

	// Handle remove appliance
	const handleRemoveAppliance = (applianceId: number) => {
		setSelectedAppliances(prev => prev.filter(item => item.id !== applianceId));
	};

	// Handle save & continue
	const handleSaveAndContinue = () => {
		const submissionData: AssessmentApplianceSubmissionModel = {
			recommendationId,
			appliances: selectedAppliances,
		};
		// onSubmit?.(submissionData);
		saveAssessment(submissionData)
			.unwrap()
			.then(() => {
				toast.success("Physical assessment status saved successfully");
				onComplete();
				onClose();
			})
			.catch(err => toast.error(getErrorMessage(err?.data)));
	};

	// Handle complete
	const handleComplete = () => {
		const submissionData: AssessmentApplianceSubmissionModel = {
			recommendationId,
			appliances: selectedAppliances,
		};
		carryoutPhysicalLoadAssessment(submissionData)
			.unwrap()
			.then(() => {
				toast.success("Physical assessment status updated successfully");
				onComplete();
				onClose();
			})
			.catch(err => toast.error(getErrorMessage(err?.data)));
	};

	return (
		<div className={styles.container}>
			{/* Header */}
			<div className={styles.header}>
				<button disabled={processingAssessment || savingAssessment} onClick={onClose} className={styles.backButton}>
					<span className={styles.backIcon}>←</span>
				</button>
				<div className={styles.headerContent}>
					<h1 className={styles.title}>List of Appliances</h1>
					<p className={styles.subtitle}>Search and select an appliance to continue.</p>
				</div>
			</div>

			{/* Search Bar */}
			<div className={styles.searchContainer}>
				<div className={styles.searchInputWrapper}>
					<span className={styles.searchIcon}>
						<SearchIcon />
					</span>
					<input
						type="text"
						placeholder="Search and select an appliance to continue."
						value={searchTerm}
						onChange={e => setSearchTerm(e.target.value)}
						className={styles.searchInput}
					/>
				</div>
			</div>
			<CommonLoadingSpinner loading={isLoading}>
				<div className={styles.appliancesList}>
					{filteredAppliances.map(appliance => {
						const isSelected = isApplianceSelected(appliance.id);
						const selectedData = getSelectedApplianceData(appliance.id);

						return (
							<div key={appliance.id} className={`${styles.applianceItem} ${isSelected ? styles.selectedItem : ""}`}>
								<div className={styles.applianceInfo}>
									{/* <span className={styles.applianceIcon}>{appliance.iconUrl}</span> */}
									<img className={styles.applianceIcon} src={appliance.iconUrl} alt={appliance.name} />
									{isSelected && selectedData ? (
										<div className={styles.applianceDetails}>
											<span className={styles.applianceName}>{appliance.name}</span>
											<span className={styles.applianceStats}>
												Quantity: {selectedData.quantity} Daily Usage: {selectedData.usage} hrs
											</span>
										</div>
									) : (
										<span className={styles.applianceName}>{appliance.name}</span>
									)}
								</div>

								{isSelected ? (
									<Button
										text="Edit"
										variant="secondary"
										onClick={() => handleEditAppliance(appliance)}
										leftIcon={
											<span className={styles.editIcon}>
												<PenIcon color="white" />
											</span>
										}
									/>
								) : (
									<Button text="Add" variant="primary" onClick={() => handleAddAppliance(appliance)} />
								)}
							</div>
						);
					})}
				</div>
			</CommonLoadingSpinner>

			{/* Bottom Actions */}
			<div className={styles.bottomActions}>
				<Button
					text={savingAssessment ? "Saving..." : "Save & Continue"}
					variant="success"
					onClick={handleSaveAndContinue}
					disabled={selectedAppliances.length === 0 || processingAssessment || savingAssessment}
				/>
				<Button
					text={processingAssessment ? "Saving..." : "Complete"}
					variant="primary"
					onClick={handleComplete}
					disabled={selectedAppliances.length === 0 || processingAssessment || savingAssessment}
				/>
			</div>

			{/* Modal */}
			{isModalOpen && editingAppliance && (
				<CommonDialog onCloseDialog={() => setIsModalOpen(false)}>
					<ApplianceEditModal
						appliance={editingAppliance.appliance}
						initialData={editingAppliance.data}
						onSave={handleSaveAppliance}
						onRemove={
							editingAppliance.isNew
								? undefined
								: () => {
										handleRemoveAppliance(editingAppliance.appliance.id);
										setIsModalOpen(false);
										setEditingAppliance(null);
								  }
						}
						onCancel={() => setIsModalOpen(false)}
					/>
				</CommonDialog>
			)}
		</div>
	);
};

export default ApplianceSelector;
